import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getComments,
  hideComment,
  unhideComment,
  deleteComment,
} from "@/services/firebase/comment";
import { Comment } from "@/types/comment";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import PromptDialog from "@/components/common/PromptDialog";
import UserAvatar from "@/components/common/UserAvatar";
import CopyableId from "@/components/common/CopyableId";
import ActionMenu from "@/components/common/ActionMenu";
import { useAuth } from "@/hooks/useAuth";
import { useDebounce } from "@/hooks/useDebounce";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { MessageSquare } from "lucide-react";
import {
  AdminPage,
  AdminPageHeader,
  AdminSearchInput,
  AdminLoadingSkeleton,
  AdminErrorAlert,
  AdminEmptyState,
  AdminDataTable,
} from "@/components/admin-ui";
import { AdminTableColumn } from "@/components/admin-ui/admin-data-table";

const columns: AdminTableColumn[] = [
  { key: "user", label: "用戶" },
  { key: "content", label: "評論內容" },
  { key: "videoId", label: "影片 ID", hideBelow: "md" },
  { key: "status", label: "狀態" },
  { key: "reportCount", label: "舉報數", sortable: true, hideBelow: "lg" },
  { key: "createdAt", label: "建立時間", sortable: true, hideBelow: "lg" },
  { key: "actions", label: "操作" },
];

export default function CommentListPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [filterStatus, setFilterStatus] = useState<"all" | "visible" | "hidden">("all");
  const [pageSize, setPageSize] = useState<number>(20);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    id: string | null;
    content: string;
  }>({ isOpen: false, id: null, content: "" });
  const [hidePrompt, setHidePrompt] = useState<{
    isOpen: boolean;
    id: string | null;
  }>({ isOpen: false, id: null });

  const { data: comments, isLoading, error } = useQuery({
    queryKey: ["comments", filterStatus],
    queryFn: () => {
      const filters: any = { isDeleted: false };
      if (filterStatus === "visible") {
        filters.isHidden = false;
      } else if (filterStatus === "hidden") {
        filters.isHidden = true;
      }
      return getComments(filters);
    },
  });

  const hideMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      await hideComment(id, reason, user?.uid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments"] });
      setActionError(null);
    },
    onError: (error) => {
      console.error("隱藏評論失敗:", error);
      setActionError(error instanceof Error ? error.message : "未知錯誤");
    },
  });

  const unhideMutation = useMutation({
    mutationFn: (id: string) => unhideComment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments"] });
      setActionError(null);
    },
    onError: (error) => {
      console.error("顯示評論失敗:", error);
      setActionError(error instanceof Error ? error.message : "未知錯誤");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteComment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments"] });
      setDeleteConfirm({ isOpen: false, id: null, content: "" });
      setActionError(null);
    },
    onError: (error) => {
      console.error("刪除評論失敗:", error);
      setActionError(error instanceof Error ? error.message : "未知錯誤");
      setDeleteConfirm({ isOpen: false, id: null, content: "" });
    },
  });

  const filteredComments = useMemo(() => {
    if (!comments || comments.length === 0) return [];

    return comments.filter((comment) => {
      const searchLower = debouncedSearchTerm.toLowerCase();
      const matchesSearch =
        !searchLower ||
        comment.content.toLowerCase().includes(searchLower) ||
        comment.userName.toLowerCase().includes(searchLower) ||
        comment.userId.toLowerCase().includes(searchLower) ||
        comment.videoId.toLowerCase().includes(searchLower);

      return matchesSearch;
    });
  }, [comments, debouncedSearchTerm]);

  const handleHide = (id: string) => {
    setHidePrompt({ isOpen: true, id });
  };

  const confirmHide = (reason: string) => {
    if (hidePrompt.id) {
      hideMutation.mutate({ id: hidePrompt.id, reason: reason || undefined });
      setHidePrompt({ isOpen: false, id: null });
    }
  };

  const handleUnhide = (id: string) => {
    unhideMutation.mutate(id);
  };

  const handleDelete = (id: string, content: string) => {
    setDeleteConfirm({ isOpen: true, id, content });
  };

  const confirmDelete = () => {
    if (deleteConfirm.id) {
      deleteMutation.mutate(deleteConfirm.id);
    }
  };

  if (isLoading) {
    return (
      <AdminPage>
        <AdminPageHeader title="評論管理" />
        <AdminLoadingSkeleton />
      </AdminPage>
    );
  }

  if (error) {
    return (
      <AdminPage>
        <AdminPageHeader title="評論管理" />
        <AdminErrorAlert
          message={`載入評論時發生錯誤：${error instanceof Error ? error.message : "未知錯誤"}`}
        />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader title="評論管理" />

      {actionError && (
        <AdminErrorAlert
          message={`操作失敗：${actionError}`}
          onDismiss={() => setActionError(null)}
        />
      )}

      {/* 操作欄 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-4 flex-1">
          <AdminSearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="搜尋評論內容、用戶名稱、用戶 ID 或影片 ID..."
            className="max-w-md"
          />

          {/* 狀態篩選 */}
          <div className="flex gap-1.5">
            {(["all", "visible", "hidden"] as const).map((status) => {
              const labels = { all: "全部", visible: "顯示中", hidden: "已隱藏" };
              return (
                <Button
                  key={status}
                  variant={filterStatus === status ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterStatus(status)}
                >
                  {labels[status]}
                </Button>
              );
            })}
          </div>
        </div>

        {/* 每頁筆數 */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground whitespace-nowrap">每頁</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="h-8 w-[80px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="200">200</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* 評論列表 */}
      <AdminDataTable<Comment>
        columns={columns}
        data={filteredComments}
        pageSize={pageSize}
        defaultSort={{ key: "createdAt", dir: "desc" }}
        emptyIcon={<MessageSquare className="h-5 w-5" />}
        emptyTitle={
          searchTerm || filterStatus !== "all"
            ? "沒有符合條件的評論"
            : "尚無評論資料"
        }
        getRowId={(item) => item.id}
        renderCell={(comment, col) => {
          switch (col.key) {
            case "user":
              return (
                <div className="flex items-center gap-3">
                  <UserAvatar
                    user={{
                      id: comment.userId,
                      displayName: comment.userName,
                      photoURL: comment.userPhotoURL,
                      coin: 0,
                      is_member: false,
                    }}
                    size="sm"
                  />
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {comment.userName}
                    </div>
                    <CopyableId id={comment.userId} maxLength={8} size="xs" />
                  </div>
                </div>
              );
            case "content":
              return (
                <div className={comment.isHidden ? "opacity-60" : ""}>
                  <div className="text-sm text-foreground max-w-md">
                    {comment.content}
                  </div>
                  {comment.isEdited && (
                    <span className="text-xs text-muted-foreground">
                      （已編輯）
                    </span>
                  )}
                </div>
              );
            case "videoId":
              return <CopyableId id={comment.videoId} maxLength={12} />;
            case "status":
              return comment.isHidden ? (
                <Badge variant="warning">
                  已隱藏
                </Badge>
              ) : (
                <Badge variant="success">
                  顯示中
                </Badge>
              );
            case "reportCount":
              return (
                <span className="text-muted-foreground">
                  {comment.reportCount || 0}
                </span>
              );
            case "createdAt":
              return (
                <span className="text-muted-foreground">
                  {comment.createdAt
                    ? new Date(comment.createdAt).toLocaleString("zh-TW")
                    : "-"}
                </span>
              );
            case "actions":
              return (
                <ActionMenu
                  actions={[
                    comment.isHidden
                      ? {
                          label: "顯示",
                          onClick: () => handleUnhide(comment.id),
                          disabled: unhideMutation.isPending,
                        }
                      : {
                          label: "隱藏",
                          onClick: () => handleHide(comment.id),
                          disabled: hideMutation.isPending,
                        },
                    {
                      label: "刪除",
                      onClick: () => handleDelete(comment.id, comment.content),
                      disabled: deleteMutation.isPending,
                      variant: "danger",
                    },
                  ]}
                />
              );
            default:
              return null;
          }
        }}
      />

      {/* 刪除確認對話框 */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="確認刪除"
        message={`確定要刪除此評論嗎？此操作無法復原。\n\n評論內容：${deleteConfirm.content.length > 50 ? `${deleteConfirm.content.substring(0, 50)}...` : deleteConfirm.content}`}
        confirmText="刪除"
        cancelText="取消"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, content: "" })}
      />

      {/* 隱藏原因輸入對話框 */}
      <PromptDialog
        isOpen={hidePrompt.isOpen}
        title="隱藏評論"
        message="請輸入隱藏原因（可選）："
        placeholder="輸入隱藏原因..."
        confirmText="確認"
        cancelText="取消"
        onConfirm={confirmHide}
        onCancel={() => setHidePrompt({ isOpen: false, id: null })}
      />
    </AdminPage>
  );
}
